/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { intentsDenormalizer } from 'src/luis/modules/intents/intentsDenormalizer';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { HierarchicalEntity } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { entitiesDenormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { getChildById } from 'src/luis/projects/entities/utils/simpleEntityHelpers/simpleEntityHelpers';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { EntityLabel, Utterance } from 'src/luis/projects/utterances/types/Utterance';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { PhraseListStore } from 'src/luis/types/stores/features/PhraseListStore';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';

type UtteranceDenormalizerInput = {
    entityStores: EntityStore[];
    intentStores: IntentStore[];
    phraseListStores: PhraseListStore[];
    utterancePrediction: UtterancePredictionsStore;
};

/**
 * Denormalizes the utterance and prediction stores into the Utterance type.
 */
export const utteranceDenormalizer = ({
    entityStores,
    intentStores,
    utterancePrediction,
    phraseListStores
}: UtteranceDenormalizerInput): Utterance => {
    const { predictionStore, utteranceStore } = utterancePrediction;
    const entities = entitiesDenormalizer(entityStores, intentStores, phraseListStores);
    const intents = intentsDenormalizer(intentStores, entityStores, phraseListStores);

    const labeledEntities = utteranceStore.labeledEntities.map(labelDenormalizer(entities)).filter(Boolean);
    const labeledIntent = intents.find(({ id }) => utteranceStore.labeledIntentId === id);

    if (!predictionStore) {
        return {
            ...utteranceStore,
            modifiedDate: utteranceStore.modifiedDate,
            labeledIntent,
            labeledEntities: [],
            predictedIntents: [],
            predictedEntities: []
        };
    }

    return {
        ...utteranceStore,
        modifiedDate: utteranceStore.modifiedDate,
        labeledIntent,
        labeledEntities,
        predictedIntents: predictionStore.predictedIntents.map(intentScore => ({
            intent: intents.find(({ id }) => intentScore.intentId === id),
            score: intentScore.score
        })),
        predictedEntities: predictionStore.predictedEntities.map(labelDenormalizer(entities)).filter(Boolean)
    };
};

/**
 * Denormalizes the EntityLabelStore to EntityLabel object.
 */
export const labelDenormalizer = (entities: Entity[]) => (entityLabelStore: EntityLabelStore): EntityLabel => {
    let entity = entities.find(({ id }) => entityLabelStore.entityId === id);
    let role: EntityRole;
    if (!entity) {
        for (const e of entities) {
            if (e.typeId === ModelType.Hierarchical) {
                const foundChild = (<HierarchicalEntity>e).children.find(child => entityLabelStore.entityId === child.id);
                if (foundChild) {
                    entity = { ...foundChild, name: `${e.name}::${foundChild.name}`, roles: [], labelsCount: 0 };
                    break;
                }
            } else if (e.typeId === ModelType.Simple) {
                const foundChild = getChildById(entities, e.id, entityLabelStore.entityId);
                if (foundChild) {
                    entity = foundChild;
                    break;
                }
            } else if (e.typeId === ModelType.Composite) {
                const foundChild = (<CompositeEntity>e).children.find(child => entityLabelStore.entityId === child.id);
                if (foundChild) {
                    entity = foundChild;
                    break;
                }
            }
        }
    } else {
        role = entity.roles.find(({ id }) => entityLabelStore.roleId === id);
    }

    if (!entity) {
        return undefined;
    }

    return {
        role,
        entity,
        endCharIndex: entityLabelStore.endCharIndex,
        endTokenIndex: entityLabelStore.endTokenIndex,
        startCharIndex: entityLabelStore.startCharIndex,
        startTokenIndex: entityLabelStore.startTokenIndex
    };
};
